import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminGrievanceManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for grievances data
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [limit] = useState(10);

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);

  // Form state for response
  const [responseData, setResponseData] = useState({
    status: '',
    response: ''
  });

  const [submitting, setSubmitting] = useState(false);

  // Check if mobile on mount and resize (used for responsive content layout)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch grievances from API
  const fetchGrievances = async (page = 1, search = '', status = '', type = '', source = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && search.trim() && { search: search.trim() }),
        ...(status && { status }),
        ...(type && { type }),
        ...(source && { source })
      });

      const apiUrl = `${API_LINK}/api/admin/grievances?${params}`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Admin API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();

      if (result.success) {
        const grievancesData = result.data?.grievances || [];
        const paginationData = result.data?.pagination || {};

        setGrievances(grievancesData);
        setCurrentPage(paginationData.currentPage || page);
        setTotalPages(paginationData.totalPages || 1);
        setTotalCount(paginationData.totalCount || grievancesData.length);
      } else {
        setError(result.message || 'Failed to fetch grievances from server');
      }
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load grievances: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchGrievances(1, '', '', '', '');
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle search and filters
  const handleSearch = () => {
    setCurrentPage(1);
    fetchGrievances(1, searchTerm, statusFilter, typeFilter, sourceFilter);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page && page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchGrievances(page, searchTerm, statusFilter, typeFilter, sourceFilter);
    }
  };

  // Handle response submission
  const handleResponseSubmit = async () => {
    if (!selectedGrievance || !responseData.status) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `${API_LINK}/api/admin/grievances/${selectedGrievance.grievance_source}/${selectedGrievance.grievance_id}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(responseData)
      });

      const result = await response.json();

      if (result.success) {
        setShowResponseModal(false);
        setSelectedGrievance(null);
        setResponseData({ status: '', response: '' });
        fetchGrievances(currentPage || 1, searchTerm, statusFilter, typeFilter, sourceFilter);
        alert('Grievance updated successfully!');
      } else {
        alert(result.message || 'Update failed');
      }
    } catch (error) {
      alert('Failed to update grievance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedGrievance) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/grievances/${selectedGrievance.grievance_source}/${selectedGrievance.grievance_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedGrievance(null);
        fetchGrievances(currentPage || 1, searchTerm, statusFilter, typeFilter, sourceFilter);
        alert('Grievance deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      alert('Failed to delete grievance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle view click
  const handleViewClick = (grievance) => {
    setSelectedGrievance(grievance);
    setShowViewModal(true);
  };

  // Handle response click
  const handleResponseClick = (grievance) => {
    setSelectedGrievance(grievance);
    setResponseData({
      status: grievance.status || 'pending',
      response: grievance.response || ''
    });
    setShowResponseModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (grievance) => {
    setSelectedGrievance(grievance);
    setShowDeleteModal(true);
  };



  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3cd', color: '#856404', border: '#ffeaa7' };
      case 'in-progress':
        return { bg: '#cce5ff', color: '#004085', border: '#80bdff' };
      case 'resolved':
        return { bg: '#d4edda', color: '#155724', border: '#c3e6cb' };
      default:
        return { bg: '#f8f9fa', color: '#6c757d', border: '#dee2e6' };
    }
  };

  // Get type badge color
  const getTypeColor = (type) => {
    switch (type) {
      case 'academic':
        return { bg: '#e3f2fd', color: '#1976d2' };
      case 'administrative':
        return { bg: '#f3e5f5', color: '#7b1fa2' };
      case 'facilities':
        return { bg: '#e8f5e8', color: '#388e3c' };
      case 'examination':
        return { bg: '#fff3e0', color: '#f57c00' };
      case 'financial':
        return { bg: '#ffebee', color: '#d32f2f' };
      default:
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-grievances" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Grievance Management Portal ❗
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage all student and teacher grievances, respond to complaints, and track resolution status.
        </p>
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        {/* Search and Filters Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto auto auto auto',
          gap: '1rem',
          marginBottom: '1.5rem',
          alignItems: 'end'
        }}>
          {/* Search Bar */}
          <div style={{ position: 'relative' }}>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Search Grievances
            </label>
            <input
              type="text"
              placeholder="Search by subject, submitter, or grievance ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{
                width: '100%',
                padding: '0.75rem 3rem 0.75rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#238548';
                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            />
            <button
              onClick={handleSearch}
              style={{
                position: 'absolute',
                right: '10px',
                top: '32px',
                background: 'none',
                border: 'none',
                fontSize: '1.1rem',
                cursor: 'pointer',
                color: '#666',
                padding: '0.5rem'
              }}
            >
              🔍
            </button>
          </div>

          {/* Status Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                minWidth: '120px',
                backgroundColor: '#ffffff'
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                minWidth: '140px',
                backgroundColor: '#ffffff'
              }}
            >
              <option value="">All Types</option>
              <option value="academic">Academic</option>
              <option value="administrative">Administrative</option>
              <option value="facilities">Facilities</option>
              <option value="examination">Examination</option>
              <option value="financial">Financial</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Source Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Source
            </label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              style={{
                padding: '0.75rem',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                minWidth: '120px',
                backgroundColor: '#ffffff'
              }}
            >
              <option value="">All Sources</option>
              <option value="student">Student</option>
              <option value="teacher">Teacher</option>
            </select>
          </div>

          {/* Apply Filters Button */}
          <button
            onClick={handleSearch}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Apply Filters
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? 'Loading grievances...' :
              error ? 'Error loading grievances' :
                `Showing ${grievances.length} of ${totalCount} grievances`}
            {(searchTerm || statusFilter || typeFilter || sourceFilter) && !loading && !error && ' (filtered)'}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Grievances</p>
          <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
          <button
            onClick={() => fetchGrievances(currentPage, searchTerm, statusFilter, typeFilter, sourceFilter)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Grievances Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && grievances.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading grievances...</p>
            <style>
              {`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}
            </style>
          </div>
        ) : grievances.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>❗</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {(searchTerm || statusFilter || typeFilter || sourceFilter) ? 'No grievances found' : 'No grievances yet'}
            </h3>
            <p style={{ color: '#666', margin: '0', fontSize: '1rem' }}>
              {(searchTerm || statusFilter || typeFilter || sourceFilter)
                ? 'Try adjusting your search criteria or filters'
                : 'No grievances have been submitted yet'
              }
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>ID</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Subject</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Submitter</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Type</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Date</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grievances.map((grievance, index) => {
                    const statusStyle = getStatusColor(grievance.status);
                    const typeStyle = getTypeColor(grievance.grievance_type);

                    return (
                      <tr key={grievance.grievance_id} style={{
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.3s ease'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '1rem', fontWeight: 500 }}>
                          <div>
                            <div style={{ color: '#238548', fontWeight: 600, marginBottom: '0.25rem' }}>
                              {grievance.grievance_id}
                            </div>
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#666',
                              padding: '0.2rem 0.5rem',
                              backgroundColor: grievance.grievance_source === 'student' ? '#e3f2fd' : '#f3e5f5',
                              color: grievance.grievance_source === 'student' ? '#1976d2' : '#7b1fa2',
                              borderRadius: '12px',
                              textTransform: 'uppercase',
                              display: 'inline-block'
                            }}>
                              {grievance.grievance_source}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem', maxWidth: '200px' }}>
                          <div style={{
                            fontWeight: 500,
                            color: '#333',
                            marginBottom: '0.25rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {grievance.subject}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {grievance.description}
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <div>
                            <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                              {grievance.submitter_name || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {grievance.submitter_id}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: typeStyle.bg,
                            color: typeStyle.color,
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'capitalize'
                          }}>
                            {grievance.grievance_type}
                          </span>
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            border: `1px solid ${statusStyle.border}`
                          }}>
                            {grievance.status}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', color: '#333' }}>
                          {formatDate(grievance.created_at)}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => handleViewClick(grievance)}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#e8f5e8',
                                color: '#2e7d32',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#2e7d32';
                                e.target.style.color = '#ffffff';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#e8f5e8';
                                e.target.style.color = '#2e7d32';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => handleResponseClick(grievance)}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#1976d2';
                                e.target.style.color = '#ffffff';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#e3f2fd';
                                e.target.style.color = '#1976d2';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              💬 Respond
                            </button>
                            <button
                              onClick={() => handleDeleteClick(grievance)}
                              style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#ffebee',
                                color: '#d32f2f',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#d32f2f';
                                e.target.style.color = '#ffffff';
                                e.target.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#ffebee';
                                e.target.style.color = '#d32f2f';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total grievances
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Grievance Modal */}
      {
        showViewModal && selectedGrievance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              width: '100%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  👁️ View Grievance
                </h3>
                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                  Grievance ID: {selectedGrievance.grievance_id}
                </p>
              </div>

              {/* Modal Content */}
              <div style={{
                padding: '2rem',
                overflowY: 'auto',
                flex: 1
              }}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Basic Info */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        Submitter
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                          {selectedGrievance.submitter_name || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                          {selectedGrievance.submitter_id} ({selectedGrievance.grievance_source})
                        </div>
                      </div>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        Status & Type
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <span style={{
                            ...getStatusColor(selectedGrievance.status),
                            padding: '0.2rem 0.6rem',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'capitalize'
                          }}>
                            {selectedGrievance.status}
                          </span>
                        </div>
                        <span style={{
                          ...getTypeColor(selectedGrievance.grievance_type),
                          padding: '0.2rem 0.6rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}>
                          {selectedGrievance.grievance_type}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Subject
                    </label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      fontWeight: 500
                    }}>
                      {selectedGrievance.subject}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Description
                    </label>
                    <div style={{
                      padding: '0.75rem',
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      minHeight: '80px',
                      lineHeight: 1.6
                    }}>
                      {selectedGrievance.description}
                    </div>
                  </div>

                  {/* Response (if any) */}
                  {selectedGrievance.response && (
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        Admin Response
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #bbf7d0',
                        minHeight: '60px',
                        lineHeight: 1.6
                      }}>
                        {selectedGrievance.response}
                      </div>
                    </div>
                  )}

                  {/* Dates */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: '#374151',
                        marginBottom: '0.5rem'
                      }}>
                        Submitted Date
                      </label>
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        {formatDate(selectedGrievance.created_at)}
                      </div>
                    </div>

                    {selectedGrievance.resolved_at && (
                      <div>
                        <label style={{
                          display: 'block',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: '0.5rem'
                        }}>
                          Resolved Date
                        </label>
                        <div style={{
                          padding: '0.75rem',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}>
                          {formatDate(selectedGrievance.resolved_at)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                backgroundColor: '#f8fafc'
              }}>
                <button
                  onClick={() => setShowViewModal(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleResponseClick(selectedGrievance);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 600
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  💬 Respond to Grievance
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Response Modal */}
      {
        showResponseModal && selectedGrievance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '1.5rem 2rem',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  💬 Respond to Grievance
                </h3>
                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                  Update status and provide response
                </p>
              </div>

              {/* Modal Content */}
              <div style={{
                padding: '2rem',
                overflowY: 'auto',
                flex: 1
              }}>
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {/* Grievance Summary */}
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                      <strong>Subject:</strong> {selectedGrievance.subject}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      <strong>From:</strong> {selectedGrievance.submitter_name} ({selectedGrievance.submitter_id})
                    </div>
                  </div>

                  {/* Status Update */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Update Status *
                    </label>
                    <select
                      value={responseData.status}
                      onChange={(e) => setResponseData({ ...responseData, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  {/* Response Text */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Admin Response
                    </label>
                    <textarea
                      value={responseData.response}
                      onChange={(e) => setResponseData({ ...responseData, response: e.target.value })}
                      placeholder="Provide your response to this grievance..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        resize: 'vertical',
                        minHeight: '120px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#238548'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                      Provide a clear and helpful response to address the grievance
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                backgroundColor: '#f8fafc'
              }}>
                <button
                  onClick={() => {
                    setShowResponseModal(false);
                    setResponseData({ status: '', response: '' });
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResponseSubmit}
                  disabled={submitting || !responseData.status}
                  style={{
                    padding: '0.75rem 2rem',
                    border: 'none',
                    background: submitting || !responseData.status
                      ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                      : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: submitting || !responseData.status ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting && responseData.status) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting && responseData.status) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Updating...
                    </>
                  ) : (
                    '💾 Update Grievance'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && selectedGrievance && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
              maxWidth: '400px',
              width: '100%'
            }}>
              {/* Modal Header */}
              <div style={{
                padding: '2rem 2rem 1rem 2rem',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: '#fee2e2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '2rem'
                }}>
                  🗑️
                </div>
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#dc2626'
                }}>
                  Delete Grievance
                </h3>
                <p style={{
                  margin: '0 0 1rem 0',
                  color: '#6b7280',
                  lineHeight: 1.6
                }}>
                  Are you sure you want to delete this grievance from <strong style={{ color: '#374151' }}>{selectedGrievance.submitter_name}</strong>?
                </p>
                <p style={{
                  margin: 0,
                  color: '#dc2626',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}>
                  This action cannot be undone and will permanently remove all grievance data.
                </p>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '1rem 2rem 2rem 2rem',
                display: 'flex',
                justifyContent: 'center',
                gap: '1rem'
              }}>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedGrievance(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 500
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = '#ffffff';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem 1.5rem',
                    border: 'none',
                    background: submitting
                      ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                      : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                    color: '#ffffff',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {submitting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Deleting...
                    </>
                  ) : (
                    '🗑️ Delete Grievance'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </Layout>
  );
};

export default AdminGrievanceManagement;